import { useAuthFormStore } from '@/stores/authForm'
import { useClaimStore } from '@/stores/claim'
import { useClaimInfoStore } from '@/stores/claimInfo'
import { useClaimsStore } from '@/stores/claims'
import { useContractStore } from '@/stores/contract'
import { useContractsStore } from '@/stores/contracts'
import { useDocumentsStore } from '@/stores/documents'
import { useDocumentsToSignStore } from '@/stores/documentsToSign'
import { useErrorStore } from '@/stores/error'
import { useLifeProcedureStore } from '@/stores/lifeProcedure'
import { useNotificationsStore } from '@/stores/notifications'
import { usePremiumDueNoticesStore } from '@/stores/premiumDueNotices'
import { useSuccessStore } from '@/stores/success'
import type { Company, CompanyMember, Me, MeToken, PendingClaim, Profile } from '@/types/me'
import { MemberRole, Portfolio, ProfileType } from '@/types/me'
import { PendingType, StroobantsPortfolioIds } from '@/types/me'
import { brokerIsStroobants } from '@/utils/brokers'
import { MINIMUM_YEAR_FOR_STROOBANTS_CLAIMS } from '@/utils/claims'
import { type advisor, pca } from '@/utils/pca'
import { resetProfileScopedStores } from '@/utils/resetStores'
import { RequestStatus, handleRequest, initialRequestState } from '@/utils/restStore'
import type { RequestState } from '@/utils/restStore'
import { defineStore } from 'pinia'

import Api from '@/services/api'

interface bookameetingParamsB2b {
  firstName: string
  lastName: string
  email: string
  phone: string
  advisorFirstname: string
  advisorLastname: string
  advisorEmail: string
}
interface State {
  me: RequestState<Me>
  meToken: RequestState<MeToken>
  bookameetingParamsB2b: bookameetingParamsB2b
  profiles: RequestState<Profile[]>
  selectedCompanyId: string | null
  selectedClientId: string | null
  selectedProfile: Profile | null
  company: RequestState<Company>
  companyMembers: RequestState<CompanyMember[]>
}

const initialState = (): State => ({
  me: initialRequestState(),
  meToken: initialRequestState(),
  bookameetingParamsB2b: {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    advisorFirstname: '',
    advisorLastname: '',
    advisorEmail: ''
  },
  profiles: initialRequestState(),
  company: initialRequestState(),
  companyMembers: initialRequestState(),
  selectedCompanyId: localStorage.getItem('companyId') || null,
  selectedClientId: localStorage.getItem('clientId') || null,
  selectedProfile: null
})

export const useMeStore = defineStore({
  id: 'me',
  state: initialState,
  actions: {
    async fetchMe() {
      await handleRequest(Api.getMe, (me: RequestState<Me>) => {
        this.me = me
        if (me.status === RequestStatus.RequestLoaded) {
          localStorage.setItem('user_locale', me.data.locale)
          this.bookameetingParamsB2b.firstName = me.data.firstName
          this.bookameetingParamsB2b.lastName = me.data.lastName
          this.bookameetingParamsB2b.email = me.data.email
          this.bookameetingParamsB2b.phone = me.data.mobilePhone ? me.data.mobilePhone.replace('+', '00') : ''
          this.bookameetingParamsB2b.advisorFirstname = `${me.data.pca?.pcaFirstName}`
          this.bookameetingParamsB2b.advisorLastname = `${me.data.pca?.pcaLastName}`
          this.bookameetingParamsB2b.advisorEmail = `${me.data.pca?.pcaEmail}`
        }
      })
      if (this.me.status === RequestStatus.RequestLoaded && brokerIsStroobants()) {
        this.me.data.pendingActions = [
          ...this.me.data.pendingActions.filter((action) => action.type !== PendingType.Claim),
          ...this.me.data.pendingActions.filter((action) => {
            if (action.type === PendingType.Claim && 'incidentDate' in action) {
              const year = Number(action.incidentDate.split('/')[2])
              return year >= MINIMUM_YEAR_FOR_STROOBANTS_CLAIMS
            }
            return false
          })
        ]
      }
    },
    async fetchToken() {
      await handleRequest(Api.getMeToken, (token: RequestState<MeToken>) => {
        this.meToken = token
      })
    },
    async fetchProfiles() {
      await handleRequest(Api.getUserProfiles, (profiles: RequestState<Profile[]>) => {
        this.profiles = profiles
        if (profiles.status === RequestStatus.RequestLoaded) {
          const selectedCompanyProfile =
            profiles.data.find((profile) => profile.company?.companyId === localStorage.getItem('companyId')) || null
          const selectedClientProfile =
            profiles.data.find((profile) => profile.clientId === localStorage.getItem('clientId')) || null

          if (this.selectedClientId && !profiles.data.find((profile) => profile.clientId === this.selectedClientId)) {
            //* reset selected client profile when logged to new user
            localStorage.removeItem('clientId')
            this.selectedClientId = null
          }
          if (this.selectedCompanyId && !selectedCompanyProfile) {
            //* reset selected company profile when logged to new user
            localStorage.removeItem('companyId')
            this.selectedCompanyId = null
          }
          if (this.selectedCompanyId && selectedCompanyProfile) {
            //* set profile
            this.selectedProfile = selectedCompanyProfile
          } else if (selectedClientProfile) {
            this.selectedProfile = selectedClientProfile
          }
          if (profiles.data.length === 1) {
            //* set profile
            this.selectedProfile = profiles.data[0]
            localStorage.setItem('clientId', this.selectedProfile.clientId as string)
            this.selectedClientId = this.selectedProfile.clientId as string
          } else {
            //* sort profiles

            const b2cProfiles = profiles.data.filter((profile) => profile.profileType === ProfileType.B2C)
            const b2bProfiles = profiles.data
              .filter(
                (
                  profile
                ): profile is Profile & { company: { companyId: string; companyName: string; legalForm: string } } =>
                  profile.profileType === ProfileType.B2B && profile.company !== undefined
              )
              .sort((a, b) => a.company.companyName.localeCompare(b.company.companyName))

            profiles.data = [...b2cProfiles, ...b2bProfiles]

            this.profiles = profiles
          }
        }
      })
    },
    async fetchCompanyMembers(companyId: string) {
      await handleRequest(
        () => Api.getCompanyMembers(companyId),
        (companyMembers: RequestState<CompanyMember[]>) => {
          this.companyMembers = companyMembers
        }
      )
    },
    async fetchCompanyDetails(companyId: string) {
      await handleRequest(
        () => Api.getCompanyDetails(companyId),
        (company: RequestState<Company>) => {
          this.company = company
        }
      )
      if (this.company.status === RequestStatus.RequestLoaded && brokerIsStroobants()) {
        this.company.data.pendingActions = [
          ...this.company.data.pendingActions.filter((PendingAction: PendingClaim) => {
            const year = Number(PendingAction.incidentDate.split('/')[2])
            return year >= MINIMUM_YEAR_FOR_STROOBANTS_CLAIMS
          })
        ]
      }
    },
    async changeSelectedProfile(profile: Profile) {
      resetProfileScopedStores([
        useAuthFormStore,
        useClaimStore,
        useClaimInfoStore,
        useClaimsStore,
        useContractStore,
        useContractsStore,
        useDocumentsStore,
        useDocumentsToSignStore,
        useErrorStore,
        useLifeProcedureStore,
        useNotificationsStore,
        usePremiumDueNoticesStore,
        useSuccessStore
      ])
      this.company = initialRequestState()
      this.companyMembers = initialRequestState()

      this.selectedProfile = profile

      if (profile.profileType === ProfileType.B2B) {
        //* set selected company profile
        localStorage.setItem('companyId', profile.company?.companyId as string)
        this.selectedCompanyId = profile.company?.companyId as string
        //* reset selected client profile
        localStorage.removeItem('clientId')
        this.selectedClientId = null

        await this.fetchCompanyDetails(this.selectedCompanyId as string)
      } else if (profile.profileType === ProfileType.B2C) {
        //* set selected client profile
        localStorage.setItem('clientId', profile.clientId as string)
        this.selectedClientId = profile.clientId as string
        //* reset selected company profile
        localStorage.removeItem('companyId')
        this.selectedCompanyId = null
      }
    },
    async updateMobilePhone(mobilePhone: string) {
      await Api.updateMobilePhone(mobilePhone)
      if (this.me.status === RequestStatus.RequestLoaded) {
        this.me.data.mobilePhone = mobilePhone
      }
    },
    async updateEmail(email: string) {
      await Api.updateEmail(email)
      if (this.me.status === RequestStatus.RequestLoaded) {
        this.me.data.email = email
      }
    },
    async postOnboarded(type: ProfileType) {
      if (this.me.status === RequestStatus.RequestLoaded) {
        if (type == ProfileType.B2C) this.me.data.b2cOnboarded = true
        if (type == ProfileType.B2B) this.me.data.b2bOnboarded = true
      }
      if (!localStorage.getItem('operatorId')) {
        await handleRequest(
          () => Api.postOnboard(type),
          () => {}
        )
      }
    }
  },
  getters: {
    selectedProfilePca: (state: State): advisor => {
      if (state.selectedClientId && state.me.status === RequestStatus.RequestLoaded) return pca(state.me.data.pca)
      else if (state.selectedCompanyId && state.company.status === RequestStatus.RequestLoaded)
        return pca(state.company.data.pca)
      else return pca(null)
    },
    marshClient: (state: State): boolean => {
      return state.me.status === RequestStatus.RequestLoaded && state.me.data.fromPortfolioId === Portfolio.Marsh
    },
    stroobantsClient: (state: State): boolean => {
      return (
        state.me.status === RequestStatus.RequestLoaded &&
        StroobantsPortfolioIds.includes(
          (state.me.data.portfolioId as Portfolio) || (state.me.data.fromPortfolioId as Portfolio)
        )
      )
    },
    account: (state: State): Me | null => {
      return state.me.status === RequestStatus.RequestLoaded ? state.me.data : null
    },
    isUnfit(state: State): boolean {
      return state.me.status === RequestStatus.RequestLoaded && state.me.data.unfit
    },
    showB2cOnboarding(state: State): boolean {
      return (
        !localStorage.getItem('operatorId') &&
        state.me.status === RequestStatus.RequestLoaded &&
        !!state.selectedClientId &&
        !state.me.data.b2cOnboarded
      )
    },
    showB2bOnboarding(state: State): boolean {
      return (
        !localStorage.getItem('operatorId') &&
        state.me.status === RequestStatus.RequestLoaded &&
        !!state.selectedCompanyId &&
        !state.me.data.b2bOnboarded
      )
    },
    selectedProfileHasAnyContract(state: State): boolean {
      if (state.selectedCompanyId)
        return state.company.status === RequestStatus.RequestLoaded && state.company.data.anyContract
      else return state.me.status === RequestStatus.RequestLoaded && state.me.data.anyContract
    },
    memberIsSignatory:
      (state: State) =>
      (companyId?: string): boolean => {
        if (!companyId && state.selectedCompanyId) companyId = state.selectedCompanyId
        if (companyId) {
          return (
            state.profiles.status === RequestStatus.RequestLoaded &&
            !!state.profiles.data
              .find((profile) => profile.company?.companyId === companyId)
              ?.roles?.includes(MemberRole.Signatory)
          )
        } else return true
      },
    getSignatoryMembers(state: State): CompanyMember[] {
      if (state.selectedCompanyId && state.companyMembers.status === RequestStatus.RequestLoaded) {
        return state.companyMembers.data.filter((member) => member.roles.includes(MemberRole.Signatory))
      } else return []
    },
    hasB2bPca(state: State): boolean {
      return state.company.status === RequestStatus.RequestLoaded && !!state.company.data.pca?.pcaTeam?.includes('B2B')
    }
  }
})
